<script setup lang="ts">
/**
 * Global ErrorBoundary — catches uncaught errors in the child component tree
 * and shows a recoverable fallback ("页面出错" + Reload) instead of letting
 * the app go blank. Logs the original error to the console for debugging.
 *
 * Usage (in main.ts):
 *   app.component('ErrorBoundary', ErrorBoundary)
 *   // then in App.vue:
 *   <ErrorBoundary><RouterView /></ErrorBoundary>
 */
import { onErrorCaptured, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { AlertTriangle, RefreshCw } from 'lucide-vue-next'
import Button from '@/components/ui/Button.vue'

const { t } = useI18n()

const error = ref<Error | null>(null)

onErrorCaptured((err: unknown) => {
  // Stop propagation so the error doesn't bubble further up the tree.
  const e = err instanceof Error ? err : new Error(String(err))
  // eslint-disable-next-line no-console
  console.error('[ErrorBoundary] captured error:', e)
  error.value = e
  return false
})

function reload(): void {
  if (typeof window !== 'undefined') {
    window.location.reload()
  }
}

function reset(): void {
  error.value = null
}
</script>

<template>
  <!-- Normal render path: pass children through untouched -->
  <template v-if="!error">
    <slot />
  </template>

  <!-- Fallback UI when a child errored -->
  <div
    v-else
    role="alert"
    aria-live="assertive"
    class="flex min-h-screen items-center justify-center bg-bg-base p-6"
  >
    <div class="w-full max-w-md rounded-lg border border-border bg-bg-surface p-8 text-center shadow-sm">
      <div
        class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-danger/10 text-danger"
        aria-hidden="true"
      >
        <AlertTriangle :size="24" />
      </div>

      <h1 class="mt-4 text-lg font-semibold text-fg">
        {{ t('errorBoundary.title') }}
      </h1>
      <p class="mt-2 text-sm text-fg-muted">
        {{ t('errorBoundary.description') }}
      </p>

      <details
        v-if="error.message"
        class="mt-4 rounded-md border border-border bg-bg-elevated/40 p-3 text-left text-xs text-fg-muted"
      >
        <summary class="cursor-pointer select-none font-medium text-fg-muted">
          {{ t('errorBoundary.details') }}
        </summary>
        <pre class="mt-2 max-h-32 overflow-auto whitespace-pre-wrap break-words font-mono">{{ error.message }}</pre>
      </details>

      <div class="mt-6 flex items-center justify-center gap-2">
        <Button variant="ghost" size="md" @click="reset">
          {{ t('errorBoundary.dismiss') }}
        </Button>
        <Button variant="primary" size="md" @click="reload">
          <template #icon><RefreshCw :size="14" /></template>
          {{ t('errorBoundary.reload') }}
        </Button>
      </div>
    </div>
  </div>
</template>